import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { getRecommendationDetails } from 'apis/RecommendationsAPI';
import ChevronRight from 'common/Icons/ChevronRight';
import GradientBorderButton from 'components/lib/GradientBorderButton';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { VerticalDivider } from 'components/lib/VerticalDivider';
import { AIInsightSolution } from 'types/recommendations';
import { parseStructureFromJSON } from 'utils/helpers/object';

import CategoryInsightSection from './CategoryInsightSection';
import {
  CATEGORY_DETAILS_TITLE,
  CATEGORY_INSIGHT_QUERY_KEY_SCOPE,
  CATEGORY_INSIGHT_SECTION_TITLE,
  CATEGORY_INSIGHT_VARIANT,
  SALES_TALK_TRACKS_LABEL,
} from './constants';
import { asStringArray, getSalesTalkTrackCategoryPath, unwrap } from './utils';

type Props = {
  recommendationId: number;
  communityId?: number;
  categoryLabel?: string;
};

const Panel = styled.div`
  margin-top: 20px;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 0 18px;
`;

const PanelTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const PanelIcon = styled.img`
  width: 22px;
  height: 22px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px 14px;
`;

const SectionCell = styled.div`
  flex: 1 1 420px;
  min-width: 0;
`;

const SectionDivider = styled(VerticalDivider)`
  flex: 0 0 1px;
`;

const getCategoryInsights = (structure: AIInsightSolution | null) => ({
  strengths: asStringArray(unwrap(structure?.strengths)),
  weaknesses: asStringArray(unwrap(structure?.weaknesses)),
  keyDifferences: asStringArray(unwrap(structure?.key_competitive_differences)),
  recommendedActions: asStringArray(unwrap(structure?.recommended_actions)),
});

const CategoryInsightSections = ({ recommendationId, communityId, categoryLabel }: Props) => {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: [CATEGORY_INSIGHT_QUERY_KEY_SCOPE, recommendationId, communityId],
    queryFn: () => getRecommendationDetails(recommendationId),
    enabled: recommendationId > 0,
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return <LoaderRibbed active />;
  }

  const communityOverview = data?.community_overviews.find((overview) => overview.community_id === communityId);
  const structuredSolution = communityOverview ? parseStructureFromJSON(communityOverview.solution) : null;
  const { strengths, weaknesses, keyDifferences, recommendedActions } = getCategoryInsights(
    structuredSolution as AIInsightSolution | null
  );
  const hasInsights =
    strengths.length > 0 || weaknesses.length > 0 || keyDifferences.length > 0 || recommendedActions.length > 0;

  if (!hasInsights) {
    return null;
  }

  return (
    <Panel data-test-id="ai-ci-category-details">
      <PanelHeader>
        <PanelTitle>
          <PanelIcon src="/icons/ai-blob.svg" alt="" />
          {categoryLabel ? `${CATEGORY_DETAILS_TITLE} for ${categoryLabel}` : CATEGORY_DETAILS_TITLE}
        </PanelTitle>
        <GradientBorderButton size="small" onClick={() => navigate(getSalesTalkTrackCategoryPath(categoryLabel))}>
          {SALES_TALK_TRACKS_LABEL} <ChevronRight fill="var(--competitor-purple)" width={16} height={16} />
        </GradientBorderButton>
      </PanelHeader>
      <Content>
        <SectionRow>
          <SectionCell>
            <CategoryInsightSection
              title={CATEGORY_INSIGHT_SECTION_TITLE.STRENGTHS}
              items={strengths}
              variant={CATEGORY_INSIGHT_VARIANT.STRENGTHS}
              categoryLabel={categoryLabel}
            />
          </SectionCell>
          <SectionDivider />
          <SectionCell>
            <CategoryInsightSection
              title={CATEGORY_INSIGHT_SECTION_TITLE.WEAKNESSES}
              items={weaknesses}
              variant={CATEGORY_INSIGHT_VARIANT.WEAKNESSES}
              categoryLabel={categoryLabel}
            />
          </SectionCell>
        </SectionRow>
        <SectionRow>
          <SectionCell>
            <CategoryInsightSection
              title={CATEGORY_INSIGHT_SECTION_TITLE.KEY_COMPETITIVE_DIFFERENCES}
              items={keyDifferences}
              variant={CATEGORY_INSIGHT_VARIANT.DIFFERENCES}
              categoryLabel={categoryLabel}
            />
          </SectionCell>
          <SectionDivider />
          <SectionCell>
            <CategoryInsightSection
              title={CATEGORY_INSIGHT_SECTION_TITLE.RECOMMENDED_ACTIONS}
              items={recommendedActions}
              variant={CATEGORY_INSIGHT_VARIANT.ACTIONS}
              categoryLabel={categoryLabel}
            />
          </SectionCell>
        </SectionRow>
      </Content>
    </Panel>
  );
};

export default CategoryInsightSections;
